import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BarChart3, RefreshCw, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const RANGE_OPTIONS = [
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
];

const rupee = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 })
    .format(Number(n || 0));

const CALLSITE_LABEL = {
  ai_chat: 'AI Assistant (chat)',
  competitor_menu_extract: 'Competitor menu extraction',
  financial_exec_summary: 'Financials · Exec summary',
  financial_explain_variance: 'Financials · Explain variance',
  dashboard_recommendation: 'Dashboard AI recommendation',
  unified_insights: 'Cross-module AI insights',
  module_insights_menu: 'Menu · AI strategy',
  module_insights_suppliers: 'Suppliers · AI negotiation',
  market_price_estimate: 'Menu · Market-price estimate',
  settlement_explain: 'Reconciliation · Settlement explain',
  variance_sidecar: 'Variance sidecar (operator)',
};

export const AdminCosts = () => {
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async (rangeDays) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/cost/overview?days=${rangeDays}`);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load cost overview');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); }, [days]);

  // Operator-only gate (matches backend). Clients don't get this page.
  if (user && user.role !== 'operator' && user.role !== 'admin') {
    return (
      <div className="p-8 text-center text-text-muted" data-testid="admin-costs-forbidden">
        Operator access required.
      </div>
    );
  }

  const grandCost = data?.total_inr ?? 0;
  const totalCalls = data?.total_calls ?? 0;
  const clients = data?.clients ?? [];
  const unattributed = data?.unattributed;

  return (
    <div className="p-6 space-y-6" data-testid="admin-costs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BarChart3 className="h-6 w-6 text-accent" />
          <div>
            <h1 className="text-xl font-semibold text-primary">Universal-Key Cost Meter</h1>
            <p className="text-xs text-text-muted mt-0.5">
              Per-client ₹ spend on GPT-5.2, Claude Haiku 4.5, Gemini via Emergent LLM Key.
              Cache-hit calls are ₹0 and not shown. Estimates are directional (₹83/USD).
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-border overflow-hidden" data-testid="admin-costs-range">
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setDays(opt.value)}
                className={`px-3 py-1.5 text-xs font-medium transition ${
                  days === opt.value
                    ? 'bg-primary text-white'
                    : 'bg-white text-text-secondary hover:bg-neutral-50'
                }`}
                data-testid={`admin-costs-range-${opt.value}d`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => load(days)}
            disabled={loading}
            className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline disabled:opacity-50"
            data-testid="admin-costs-refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Grand totals */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-border rounded-md p-5" data-testid="admin-costs-total-inr">
          <div className="text-xs uppercase tracking-wider text-text-muted">Total spend ({days}d)</div>
          <div className="text-2xl font-semibold text-primary mt-1">{rupee(grandCost)}</div>
        </div>
        <div className="bg-white border border-border rounded-md p-5" data-testid="admin-costs-total-calls">
          <div className="text-xs uppercase tracking-wider text-text-muted">LLM calls</div>
          <div className="text-2xl font-semibold text-primary mt-1">{totalCalls.toLocaleString('en-IN')}</div>
        </div>
        <div className="bg-white border border-border rounded-md p-5" data-testid="admin-costs-avg">
          <div className="text-xs uppercase tracking-wider text-text-muted">Avg ₹ / call</div>
          <div className="text-2xl font-semibold text-primary mt-1">
            {totalCalls ? rupee(grandCost / totalCalls) : rupee(0)}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-state-error/10 border border-state-error rounded-md p-3 text-sm text-state-error" data-testid="admin-costs-error">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="flex items-center gap-2 text-text-muted text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading cost data…
        </div>
      )}

      {/* Per-client rollup */}
      <div className="bg-white border border-border rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-border">
          <h2 className="font-semibold text-primary">Per-client breakdown</h2>
        </div>
        {clients.length === 0 && !loading && (
          <div className="px-5 py-6 text-center text-sm text-text-muted" data-testid="admin-costs-empty">
            No LLM calls in this window. Either no one has used AI features yet, or the cache is
            absorbing repeat loads (see /admin/costs after next cache miss).
          </div>
        )}
        {clients.map((c) => (
          <details
            key={c.client_id}
            className="border-b border-border last:border-b-0"
            data-testid={`admin-costs-client-${c.client_id}`}
          >
            <summary className="px-5 py-3 flex items-center justify-between cursor-pointer hover:bg-neutral-50 transition">
              <div className="flex items-center gap-3">
                <span className="font-medium text-primary">{c.client_name}</span>
                <span className="text-xs text-text-muted">{c.total_calls} call{c.total_calls === 1 ? '' : 's'}</span>
              </div>
              <div className="text-lg font-semibold text-primary tabular-nums">
                {rupee(c.total_inr)}
              </div>
            </summary>
            <div className="px-5 pb-4 pt-1">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-text-muted">
                  <tr>
                    <th className="text-left font-normal py-1.5">Callsite</th>
                    <th className="text-right font-normal py-1.5">Calls</th>
                    <th className="text-right font-normal py-1.5">Tokens in</th>
                    <th className="text-right font-normal py-1.5">Tokens out</th>
                    <th className="text-right font-normal py-1.5">₹ cost</th>
                  </tr>
                </thead>
                <tbody>
                  {c.by_callsite.map((row) => (
                    <tr key={row.callsite} className="border-t border-border">
                      <td className="py-1.5 text-text-secondary">
                        {CALLSITE_LABEL[row.callsite] || row.callsite}
                      </td>
                      <td className="py-1.5 text-right tabular-nums">{row.calls}</td>
                      <td className="py-1.5 text-right tabular-nums text-text-muted">{row.tokens_in.toLocaleString('en-IN')}</td>
                      <td className="py-1.5 text-right tabular-nums text-text-muted">{row.tokens_out.toLocaleString('en-IN')}</td>
                      <td className="py-1.5 text-right tabular-nums font-medium">{rupee(row.cost_inr)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        ))}
        {unattributed && (
          <div className="px-5 py-3 border-t border-border bg-neutral-50" data-testid="admin-costs-unattributed">
            <div className="flex items-center justify-between text-sm">
              <div>
                <span className="font-medium text-primary">(Unattributed)</span>
                <span className="text-xs text-text-muted ml-2">
                  Calls not linked to a specific client — e.g. the market-price estimate widget.
                </span>
              </div>
              <div className="font-medium tabular-nums">{rupee(unattributed.total_inr)}</div>
            </div>
          </div>
        )}
      </div>

      <p className="text-xs text-text-muted">
        Kept private: this page is operator-only. Clients cannot access `/admin/costs`
        or `/api/admin/cost/overview`.
      </p>
    </div>
  );
};

export default AdminCosts;
